// V36 — Tutor FAQ page (mobile-friendly, reuses BECOME_TUTOR_FAQS data).
// Lives at /tutor/faqs so the "Frequently Asked Questions" row inside the
// Tutor My-Profile menu opens an accessible search-friendly FAQ screen
// without leaving the tutor mobile shell.

import { useState, useMemo } from "react";
import { ChevronDown, Search, HelpCircle } from "lucide-react";
import TutorMobileLayout from "../components/tutor/TutorMobileLayout";
import { BECOME_TUTOR_FAQS, BECOME_TUTOR_FAQ_CATEGORIES } from "../data/becomeTutorFaqs";

export default function TutorFAQs() {
    const [openId, setOpenId] = useState(null);
    const [activeCat, setActiveCat] = useState("");
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        let list = BECOME_TUTOR_FAQS;
        if (activeCat) list = list.filter((f) => f.c === activeCat);
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            list = list.filter((f) =>
                (f.q || "").toLowerCase().includes(needle) ||
                (f.a || "").toLowerCase().includes(needle));
        }
        return list;
    }, [q, activeCat]);

    return (
        <TutorMobileLayout title="Tutor FAQs">
            {/* Hero */}
            <section className="-mx-4 -mt-4 mb-4 bg-gradient-to-br from-[#E03A2F] via-[#E84A1B] to-[#F35F1A] text-white px-5 pt-6 pb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <HelpCircle className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="font-heading text-xl font-bold leading-tight">Frequently Asked Questions</h1>
                        <p className="text-xs opacity-90 mt-0.5">{BECOME_TUTOR_FAQS.length} answers about teaching with Anand Tutorials</p>
                    </div>
                </div>
            </section>

            {/* Search */}
            <div className="mb-3 flex items-center gap-2 bg-rose-50 ring-1 ring-rose-200 rounded-full px-3 py-2 min-h-[44px]" data-testid="tutor-faqs-search">
                <Search className="w-4 h-4 text-rose-500" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search FAQs…"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-rose-400 text-[#0F172A]"
                    data-testid="tutor-faqs-search-input"
                />
            </div>

            {/* Category chips */}
            <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 mb-3 pb-1" data-testid="tutor-faqs-categories">
                <button onClick={() => setActiveCat("")} data-testid="tutor-faqs-cat-all"
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap flex-shrink-0 transition ${
                        activeCat === "" ? "bg-rose-600 text-white" : "bg-white border border-slate-200 text-slate-600"
                    }`}>
                    All
                </button>
                {BECOME_TUTOR_FAQ_CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => setActiveCat(c.id)} data-testid={`tutor-faqs-cat-${c.id}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap flex-shrink-0 transition ${
                            activeCat === c.id ? "bg-rose-600 text-white" : "bg-white border border-slate-200 text-slate-600"
                        }`}>
                        {c.label}
                    </button>
                ))}
            </div>

            {/* Results count */}
            <div className="text-xs text-slate-500 mb-3" data-testid="tutor-faqs-count">
                {filtered.length} {filtered.length === 1 ? "answer" : "answers"} {q && <span>matching “{q}”</span>}
            </div>

            {/* FAQ accordion */}
            <div className="space-y-2" data-testid="tutor-faqs-list">
                {filtered.length === 0 ? (
                    <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-sm text-slate-500">
                        No FAQs match your search.
                    </div>
                ) : filtered.map((f, idx) => {
                    const id = `${f.c}-${idx}`;
                    const isOpen = openId === id;
                    return (
                        <div key={id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`tutor-faq-${id}`}>
                            <button
                                onClick={() => setOpenId(isOpen ? null : id)}
                                className="w-full flex items-start gap-3 px-4 py-3.5 min-h-[56px] text-left active:bg-slate-50 transition"
                                data-testid={`tutor-faq-q-${id}`}
                            >
                                <span className="flex-1 text-[14px] font-semibold text-[#0F172A] leading-snug">{f.q}</span>
                                <ChevronDown className={`w-5 h-5 text-slate-500 shrink-0 mt-0.5 transition ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                            {isOpen && (
                                <div className="px-4 pb-4 text-[13px] text-slate-700 leading-relaxed border-t border-slate-100 pt-3" data-testid={`tutor-faq-a-${id}`}>
                                    {f.a}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </TutorMobileLayout>
    );
}
